import React, { useState, useMemo, useEffect, useRef } from 'react';
import { db } from '../data/store';
import { SERVER_URL, MAPI_URL, syncedGet, syncedSet } from '../data/serverStore';
import { printSampleLabel } from '../data/labelPrinter';
import { useApprovalCheck } from './ApprovalGate';
import { resultsWithCategoryUnits } from '../data/categoryUnitDefaults';

const FILTERS = ['All','Fuel','Specific','Water','Coal'];
const COMPLETION_FILTERS = ['Uncompleted','Completed','All'];
const today = () => new Date().toISOString().split('T')[0];
const weekOut = () => new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

// ── Add a missed COC from Log Report ───────────────────────────────────────
// COCs are work-order records: attaching one here makes it available to every
// sample/report/email in the same Work Order. Both the image and the link are
// persisted by the VPS, not retained only in this browser.
function AddCOCModal({ sample, toast, onClose, onAttached }) {
  const [working, setWorking] = useState(false);
  const [status, setStatus] = useState('');
  const [scanners, setScanners] = useState([]);
  const [showScanPicker, setShowScanPicker] = useState(false);
  const fileInputRef = useRef(null);

  const registerCOC = async (cocId) => {
    const workOrder = String(sample?.workOrder || '').trim();
    if (!workOrder) throw new Error('This sample does not have a Work Order.');
    const map = syncedGet('coc_by_wo', {}) || {};
    const existing = Array.isArray(map[workOrder]) ? map[workOrder] : [];
    const merged = [...new Set([...existing, cocId].filter(Boolean))];
    await syncedSet('coc_by_wo', { ...map, [workOrder]: merged });
    onAttached?.(cocId);
    toast('success', `COC attached to Work Order ${workOrder}`);
    onClose();
  };

  const handleScan = async () => {
    setStatus('Looking for connected scanners...');
    try {
      // Match Sample Receiving exactly: identify the Windows Agent running on
      // this computer, then enumerate scanners visible to that workstation.
      const agentRes = await fetch(`${MAPI_URL}/health`);
      const agent = await agentRes.json();
      if (!agent.workstation) throw new Error('Windows scanner workstation was not detected.');

      const listRes = await fetch(
        `${SERVER_URL}/list-scanners?workstation=${encodeURIComponent(agent.workstation)}`
      );
      const listData = await listRes.json();
      if (listData.error) throw new Error(listData.error);

      const found = listData.scanners || [];
      if (found.length === 0) {
        throw new Error('No scanner was found on this computer. Check it is powered on and connected, then try again.');
      }

      setScanners(found);
      setStatus('');
      if (found.length === 1) {
        await doScan(found[0]);
      } else {
        setShowScanPicker(true);
      }
    } catch (e) {
      setStatus('');
      toast('error', e.message || 'Cannot contact the Sterling Windows Agent.');
    }
  };

  const doScan = async (scanner) => {
    setShowScanPicker(false);
    setWorking(true);
    setStatus(`Scanning with: ${scanner.name}...`);
    // Keep the same 90-second client window as the proven Sample Receiving
    // path. It stays above the bridge timeout and allows time for the native
    // Windows scan dialog without falsely aborting a healthy scan.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    try {
      const res = await fetch(`${SERVER_URL}/scan-coc`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          deviceIndex:scanner.idx,
          workstation:scanner.workstation,
        }),
        signal:controller.signal,
      });
      const data = await res.json();
      if (!data.success || !data.cocId) throw new Error(data.error || 'Scan failed');
      setStatus('Saving the COC to the Work Order…');
      await registerCOC(data.cocId);
    } catch (e) {
      const timedOut = e.name === 'AbortError';
      toast('error', timedOut
        ? 'No response from the scanner after 90 seconds. If a scan dialog is open on the scanner computer, finish or cancel it there. If nothing is open, the Windows agent may be stuck — try again, and power-cycle the scanner if it keeps happening.'
        : e.message);
      setStatus('');
    } finally {
      clearTimeout(timeoutId);
      setWorking(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || working) return;
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
      toast('error', 'Upload the COC as JPG or PNG. PDF COCs are not supported by the current viewer.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast('error', 'Choose a JPG or PNG image.');
      return;
    }

    setWorking(true);
    setStatus('Uploading and saving the COC…');
    try {
      const image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Could not read the selected image.'));
        reader.readAsDataURL(file);
      });
      const res = await fetch(`${SERVER_URL}/upload-coc`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ image, fileName:file.name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success || !data.cocId) {
        throw new Error(data.error || 'The COC upload failed.');
      }
      await registerCOC(data.cocId);
    } catch (error) {
      toast('error', error.message || 'Could not upload the COC.');
      setStatus('');
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      {showScanPicker && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:3000,
          display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'white',borderRadius:8,padding:24,width:380,
            boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
            <div style={{fontWeight:700,fontSize:14,marginBottom:16,color:'#1a2744'}}>
              📷 Select Scanner
            </div>
            {scanners.map(s=>(
              <button key={s.idx} onClick={()=>doScan(s)}
                style={{display:'block',width:'100%',textAlign:'left',padding:'12px 16px',
                  marginBottom:8,border:'1px solid #cdd3e4',borderRadius:6,cursor:'pointer',
                  background:'white',fontSize:13,fontWeight:500,color:'#1a2744'}}
                onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
                onMouseLeave={e=>e.currentTarget.style.background='white'}>
                🖨 {s.name}
              </button>
            ))}
            <button onClick={()=>setShowScanPicker(false)}
              style={{width:'100%',padding:'8px',marginTop:4,border:'1px solid #cdd3e4',
                borderRadius:6,cursor:'pointer',background:'#f8f9fa',fontSize:12}}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <div className="modal-overlay" onClick={working ? undefined : onClose}>
        <div className="modal" style={{width:520,maxWidth:'94vw'}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h2>📎 Add Chain of Custody</h2>
          <button className="modal-close" disabled={working} onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{background:'#f0f6ff',border:'1px solid #aac8ee',borderRadius:5,
            padding:'10px 12px',fontSize:12,marginBottom:14}}>
            <strong>Sample:</strong> {sample.id}<br/>
            <strong>Work Order:</strong> {sample.workOrder || '—'}<br/>
            <span style={{color:'#5d6880'}}>
              The COC will be attached to this Work Order and will appear for every sample in the same group.
            </span>
          </div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            <button className="btn btn-primary" disabled={working || !sample.workOrder}
              onClick={handleScan}>
              {working ? '⏳ Working…' : '📷 Scan COC'}
            </button>
            <button className="btn" disabled={working || !sample.workOrder}
              onClick={()=>fileInputRef.current?.click()}>
              📁 Upload JPG/PNG
            </button>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png"
              onChange={handleUpload} style={{display:'none'}}/>
          </div>
          {status && (
            <div style={{marginTop:12,fontSize:11,color:'#1e3060',fontWeight:600}}>
              {status}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn" disabled={working} onClick={onClose}>Cancel</button>
        </div>
        </div>
      </div>
    </>
  );
}

// ── Work-order-scoped COC gallery ─────────────────────────────────────────
// A work order can have more than one COC scan attached (see SampleReceiving
// — every scan in a batch gets registered against the WO, not just one).
// This looks up the full list for the row's work order rather than relying
// on a single sample.cocId, and falls back to that single id for older
// samples saved before multi-COC tracking existed.
function WorkOrderCOCGallery({ workOrder, fallbackCocId, toast }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [rotation, setRotation] = useState(90);
  const [zoom, setZoom] = useState(0.5);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [, refreshCocList] = useState(0);

  const map = syncedGet('coc_by_wo', {}) || {};
  // Once a work-order mapping exists, even an empty array is authoritative.
  // This prevents a deleted legacy sample.cocId from reappearing as a broken
  // COC button after the final work-order COC is removed.
  const hasWorkOrderMapping = Object.prototype.hasOwnProperty.call(map, workOrder);
  const ids = hasWorkOrderMapping
    ? (Array.isArray(map[workOrder]) ? map[workOrder] : [])
    : (fallbackCocId ? [fallbackCocId] : []);

  if (ids.length === 0) return null;

  const currentId = ids[active];
  const currentUrl = `${SERVER_URL}/coc/${currentId}`;

  const resetView = () => { setRotation(90); setZoom(0.5); };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(currentUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `COC_${workOrder}_${active + 1}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Fall back to opening in a new tab if the blob download fails for
      // any reason (e.g. a network hiccup) — still gets the person the file.
      window.open(currentUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    const isRotated = rotation === 90 || rotation === 270;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Print COC</title>
      <style>
        @page { size: ${isRotated ? 'landscape' : 'portrait'}; margin: 0.25in; }
        body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;background:white;}
        img{
          ${isRotated ? 'max-width:95vh;max-height:95vw;' : 'max-width:95vw;max-height:95vh;'}
          transform:rotate(${rotation}deg);
        }
        @media print{ body{min-height:auto;} }
      </style></head><body>
      <img src="${currentUrl}" onload="window.print();"/>
      </body></html>`);
    win.document.close();
  };

  const handleDelete = async () => {
    if (!currentId || deleting) return;
    const label = ids.length > 1 ? `COC ${active + 1} of ${ids.length}` : 'this COC';
    if (!window.confirm(`Delete ${label} from Work Order ${workOrder}?\n\nThis removes only the selected COC. The other COCs will remain attached.`)) return;

    setDeleting(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/coc/${encodeURIComponent(currentId)}`, {
        method:'DELETE',
        credentials:'include',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ workOrder }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'The COC could not be deleted.');

      // Keep this browser's synchronized cache aligned immediately. The
      // server already made the durable change; this same-value write makes
      // every COC counter/view update without waiting for the next poll.
      const latestMap = syncedGet('coc_by_wo', {}) || {};
      await syncedSet('coc_by_wo', {
        ...latestMap,
        [workOrder]: Array.isArray(data.remainingIds) ? data.remainingIds : [],
      });

      toast?.('success', 'Selected COC deleted. Other attached COCs were not changed.');
      if (!data.remainingIds?.length) setOpen(false);
      else setActive(index => Math.min(index, data.remainingIds.length - 1));
      resetView();
      refreshCocList(value => value + 1);
    } catch (error) {
      toast?.('error', error.message || 'Could not delete the selected COC.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <button className="btn btn-sm"
        style={{background:ids.length>1?'#fff8e8':'#e8f0ff', borderColor:ids.length>1?'#e0c060':'#aac8ee',
          color:ids.length>1?'#7a5a00':'#1a4a80', fontWeight:600}}
        title={`${ids.length} COC scan${ids.length!==1?'s':''} for Work Order ${workOrder}`}
        onClick={e=>{ e.stopPropagation(); setActive(0); resetView(); setOpen(true); }}>
        📄 COC{ids.length>1?` (${ids.length})`:''}
      </button>
      {open && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.85)',zIndex:3000,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setOpen(false)}>
          <div style={{background:'white',borderRadius:8,padding:12,width:'98vw',height:'98vh',
            display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,gap:8,flexShrink:0}}>
              <strong>Chain of Custody — WO {workOrder}{ids.length>1?` (${active+1} of ${ids.length})`:''}</strong>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                {ids.length>1 && <>
                  <button onClick={()=>{setActive(a=>(a-1+ids.length)%ids.length);resetView();}}
                    style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>‹ Prev</button>
                  <button onClick={()=>{setActive(a=>(a+1)%ids.length);resetView();}}
                    style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>Next ›</button>
                </>}
                <button onClick={()=>setZoom(z=>Math.max(0.5,z-0.25))}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>−</button>
                <span style={{fontSize:12,color:'#555',minWidth:38,textAlign:'center'}}>{Math.round(zoom*100)}%</span>
                <button onClick={()=>setZoom(z=>Math.min(4,z+0.25))}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>+</button>
                <button onClick={resetView}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>Reset</button>
                <button onClick={()=>setRotation(r=>(r-90+360)%360)}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>↺</button>
                <button onClick={()=>setRotation(r=>(r+90)%360)}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>↻</button>
                <button onClick={handlePrint}
                  style={{border:'none',background:'#0078d4',color:'white',borderRadius:4,
                    padding:'4px 12px',cursor:'pointer',fontSize:12,fontWeight:600}}>
                  🖨 Print
                </button>
                <button onClick={handleDownload} disabled={downloading}
                  style={{border:'none',background:'#16a34a',color:'white',borderRadius:4,
                    padding:'4px 12px',cursor:downloading?'wait':'pointer',fontSize:12,fontWeight:600}}>
                  {downloading ? '⏳' : '⬇ Download'}
                </button>
                <button onClick={handleDelete} disabled={deleting}
                  title="Delete only the COC currently shown"
                  style={{border:'none',background:'#b83232',color:'white',borderRadius:4,
                    padding:'4px 12px',cursor:deleting?'wait':'pointer',fontSize:12,fontWeight:700}}>
                  {deleting ? '⏳ Deleting…' : '🗑 Delete COC'}
                </button>
                <button onClick={()=>setOpen(false)}
                  style={{border:'none',background:'none',fontSize:20,cursor:'pointer',color:'#666'}}>✕</button>
              </div>
            </div>
            {ids.length>1 && (
              <div style={{display:'flex',gap:6,marginBottom:8,flexWrap:'wrap',flexShrink:0}}>
                {ids.map((id,i)=>(
                  <img key={id} src={`${SERVER_URL}/coc/${id}`} alt=""
                    onClick={()=>{setActive(i);resetView();}}
                    style={{width:44,height:44,objectFit:'cover',borderRadius:4,cursor:'pointer',
                      border:i===active?'2px solid #1e3060':'1px solid #ddd'}}/>
                ))}
              </div>
            )}
            <div style={{flex:1,overflow:'auto',textAlign:'center',background:'#f4f5f7',borderRadius:6}}>
              <img src={currentUrl} alt="COC"
                style={{minWidth: zoom>1 ? `${zoom*100}%` : undefined,
                  width: zoom<=1 ? `${zoom*100}%` : undefined,
                  transform:`rotate(${rotation}deg)`,
                  transformOrigin:'center center', transition:'transform 0.3s',
                  margin: '20px auto', display:'block'}}/>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Per-sample Results Sheet gallery — view/download a tech's scanned/
// uploaded handwritten raw-results worksheet. Uploading happens from Enter
// Results or Sample Setup; this is the remote-viewing side. ───────────────
function SampleResultsSheetGallery({ sampleId }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [rotation, setRotation] = useState(90);
  const [zoom, setZoom] = useState(0.5);
  const [downloading, setDownloading] = useState(false);

  const map = syncedGet('results_sheets_by_sample', {}) || {};
  const ids = map[sampleId] || [];

  if (ids.length === 0) return null;

  const currentId = ids[active];
  const currentUrl = `${SERVER_URL}/results-sheet/${currentId}`;
  const resetView = () => { setRotation(90); setZoom(0.5); };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(currentUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ResultsSheet_${sampleId}_${active + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(currentUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    const isRotated = rotation === 90 || rotation === 270;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Print Results Sheet</title>
      <style>
        @page { size: ${isRotated ? 'landscape' : 'portrait'}; margin: 0.25in; }
        body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;background:white;}
        img{
          ${isRotated ? 'max-width:95vh;max-height:95vw;' : 'max-width:95vw;max-height:95vh;'}
          transform:rotate(${rotation}deg);
        }
        @media print{ body{min-height:auto;} }
      </style></head><body>
      <img src="${currentUrl}" onload="window.print();"/>
      </body></html>`);
    win.document.close();
  };

  return (
    <>
      <button className="btn btn-sm"
        style={{background:'#f3eaff',borderColor:'#c9a8ea',color:'#6a1b9a',fontWeight:600}}
        title={`${ids.length} results sheet${ids.length!==1?'s':''} for Sample ${sampleId}`}
        onClick={e=>{ e.stopPropagation(); setActive(0); resetView(); setOpen(true); }}>
        📄 Sheet{ids.length>1?` (${ids.length})`:''}
      </button>
      {open && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.85)',zIndex:3000,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setOpen(false)}>
          <div style={{background:'white',borderRadius:8,padding:12,width:'98vw',height:'98vh',
            display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,gap:8,flexShrink:0}}>
              <strong>Results Sheet — Sample {sampleId}{ids.length>1?` (${active+1} of ${ids.length})`:''}</strong>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                {ids.length>1 && <>
                  <button onClick={()=>{setActive(a=>(a-1+ids.length)%ids.length);resetView();}}
                    style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>‹ Prev</button>
                  <button onClick={()=>{setActive(a=>(a+1)%ids.length);resetView();}}
                    style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>Next ›</button>
                </>}
                <button onClick={()=>setZoom(z=>Math.max(0.5,z-0.25))}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>−</button>
                <span style={{fontSize:12,color:'#555',minWidth:38,textAlign:'center'}}>{Math.round(zoom*100)}%</span>
                <button onClick={()=>setZoom(z=>Math.min(4,z+0.25))}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>+</button>
                <button onClick={()=>setRotation(r=>(r-90+360)%360)}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>↺</button>
                <button onClick={()=>setRotation(r=>(r+90)%360)}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>↻</button>
                <button onClick={handlePrint}
                  style={{border:'none',background:'#0078d4',color:'white',borderRadius:4,
                    padding:'4px 12px',cursor:'pointer',fontSize:12,fontWeight:600}}>
                  🖨 Print
                </button>
                <button onClick={handleDownload} disabled={downloading}
                  style={{border:'none',background:'#16a34a',color:'white',borderRadius:4,
                    padding:'4px 12px',cursor:downloading?'wait':'pointer',fontSize:12,fontWeight:600}}>
                  {downloading ? '⏳' : '⬇ Download'}
                </button>
                <button onClick={()=>setOpen(false)}
                  style={{border:'none',background:'none',fontSize:20,cursor:'pointer',color:'#666'}}>✕</button>
              </div>
            </div>
            {ids.length>1 && (
              <div style={{display:'flex',gap:6,marginBottom:8,flexWrap:'wrap',flexShrink:0}}>
                {ids.map((id,i)=>(
                  <img key={id} src={`${SERVER_URL}/results-sheet/${id}`} alt=""
                    onClick={()=>{setActive(i);resetView();}}
                    style={{width:44,height:44,objectFit:'cover',borderRadius:4,cursor:'pointer',
                      border:i===active?'2px solid #6a1b9a':'1px solid #ddd'}}/>
                ))}
              </div>
            )}
            <div style={{flex:1,overflow:'auto',textAlign:'center',background:'#f4f5f7',borderRadius:6}}>
              <img src={currentUrl} alt="Results Sheet"
                style={{minWidth: zoom>1 ? `${zoom*100}%` : undefined,
                  width: zoom<=1 ? `${zoom*100}%` : undefined,
                  transform:`rotate(${rotation}deg)`,
                  transformOrigin:'center center', transition:'transform 0.3s',
                  margin: '20px auto', display:'block'}}/>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function LogReport({ onNavigate, onSelectSample, toast, session }) {
  const isAdmin = session?.role === 'admin';
  const [samples, setSamples] = useState(() => db.getSamples());
  const { check: approvalCheck, modal: approvalModal } = useApprovalCheck(session);
  const [search, setSearch] = useState({ id:'', workOrder:'', source:'', desc:'', tech:'' });
  const [filter, setFilter] = useState('All');
  const [completionFilter, setCompletionFilter] = useState('Uncompleted');
  const [sortCol, setSortCol] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingSample, setDeletingSample] = useState(false);
  const [dupModal, setDupModal] = useState(null); // sample being duplicated
  const [savingDuplicate, setSavingDuplicate] = useState(false);
  const [printingLabel, setPrintingLabel] = useState(null);
  const [addCocSample, setAddCocSample] = useState(null);
  const [cocVersion, setCocVersion] = useState(0);

  const refresh = () => setSamples(db.getSamples());
  const setS = (k, v) => setSearch(p => ({ ...p, [k]: v }));
  const clearSearch = () => setSearch({ id:'', workOrder:'', source:'', desc:'', tech:'' });

  const handlePrintLabel = async (sample, event) => {
    event?.stopPropagation();
    const key = String(sample.id);
    setPrintingLabel(key);
    try {
      const result = await printSampleLabel(sample);
      toast('success', `Label for Sample ${sample.id} sent to ${result.printer}`);
    } catch (error) {
      toast('error', error.message || 'Could not print the sample label.');
    } finally {
      setPrintingLabel(current => current === key ? null : current);
    }
  };

  // ── Auto-refresh ──────────────────────────────────────────────────────────
  // This screen used to only read db.getSamples() once on mount, so a sample
  // saved from Sample Receiving or Sample Setup wouldn't show up here until
  // a manual reload. LabBoard already solves this with a polling loop —
  // mirror that here, plus refresh immediately when the tab/window regains
  // focus so switching back right after saving feels instant rather than
  // waiting out the poll interval.
  useEffect(() => {
    const interval = setInterval(refresh, 30000);
    const onFocus = () => refresh();
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const filtered = useMemo(() => {
    let rows = [...samples];
    if (filter !== 'All') rows = rows.filter(s => s.testType?.toLowerCase() === filter.toLowerCase());
    if (completionFilter === 'Uncompleted') rows = rows.filter(s => !s.completed);
    if (completionFilter === 'Completed') rows = rows.filter(s => Boolean(s.completed));
    if (search.id)        rows = rows.filter(s => String(s.id).includes(search.id.trim()));
    if (search.workOrder) rows = rows.filter(s => s.workOrder?.toLowerCase().includes(search.workOrder.toLowerCase().trim()));
    if (search.source)    rows = rows.filter(s =>
      (s.source?.toLowerCase()||'').includes(search.source.toLowerCase().trim()) ||
      (s.client?.toLowerCase()||'').includes(search.source.toLowerCase().trim())
    );
    if (search.desc)      rows = rows.filter(s => s.desc?.toLowerCase().includes(search.desc.toLowerCase().trim()));
    if (search.tech)      rows = rows.filter(s => (s.tech||'').toLowerCase().includes(search.tech.toLowerCase().trim()));
    rows.sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [samples, filter, completionFilter, search, sortCol, sortDir]);

  const sort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  // ── Duplicate: open modal pre-filled from source sample ──────────────────
  const openDuplicate = (s, e) => {
    e.stopPropagation();
    const newId = db.getCounters().nextSampleNum; // fresh read each time modal opens
    setDupModal({
      _sourceId: s.id,
      // Reset tracking fields
      id: newId,
      // Leave this blank so Save reserves the number atomically. Showing the
      // unreserved counter value here allowed it to become stale and be reused.
      workOrder: '',
      received: today(),
      taken: today(),
      dueDate: weekOut(),
      completed: false,
      dateCompleted: '',
      billed: false,
      invoiceNum: '',
      results: {},
      // Carry over everything else
      source: s.source,
      sourceId: s.sourceId || '',
      desc: s.desc,
      client: s.client,
      attention: s.attention || '',
      phone: s.phone || '',
      po: s.po || '',
      pm: s.pm || '',
      subcontractor: s.subcontractor || '',
      testCategory: s.testCategory,
      sampleForm: s.sampleForm,
      testType: s.testType,
      tech: s.tech,
      amount: s.amount,
      solidLiq: s.solidLiq,
      rush: s.rush || '',
      price: s.price || 0,
      params: [...(s.params || [])],   // carry over all parameters
      method: s.method || '',
    });
  };

  const saveDuplicate = async () => {
    if (!dupModal || savingDuplicate) return;
    const manualWo = String(dupModal.workOrder || '').trim();
    const existingWoSamples = manualWo
      ? db.getSamples().filter(sample =>
          String(sample.workOrder || '').trim().toLowerCase() === manualWo.toLowerCase()
        )
      : [];
    if (existingWoSamples.length > 0) {
      const confirmed = window.confirm(
        `Work Order ${manualWo} already exists with Sample${existingWoSamples.length === 1 ? '' : 's'} ` +
        `${existingWoSamples.map(sample => sample.id).join(', ')}.\n\n` +
        'Continue only if this duplicate sample intentionally belongs to that same Work Order.\n' +
        'Choose Cancel and leave Work Order blank to receive a new automatic number.'
      );
      if (!confirmed) return;
    }
    setSavingDuplicate(true);
    let newId;
    let wo;
    try {
      newId = await db.nextSampleNum();
      wo = manualWo || await db.nextWorkOrder();
    } catch (error) {
      toast('error', `Could not reserve a new sample number: ${error.message}`);
      setSavingDuplicate(false);
      return;
    }
    const newSample = {
      ...dupModal,
      id: newId,
      workOrder: wo,
    };
    try {
      await db.addSample(newSample);
      onSelectSample(newSample);
      setDupModal(null);
      refresh();
      toast('success', `Sample ${newId} created from duplicate of ${dupModal._sourceId} — WO ${wo}`);
      setTimeout(() => onNavigate('receive'), 600);
    } catch (error) {
      toast('error', error.message || 'Could not create the duplicate sample');
    } finally {
      setSavingDuplicate(false);
    }
  };

  const saveEdit = () => {
    if (!editModal || savingEdit) return;
    const originalId = String(editModal._originalId ?? editModal.id);
    const newId = String(editModal.id ?? '').trim();
    const newWorkOrder = String(editModal.workOrder ?? '').trim();
    if (!newId) {
      toast('error', 'Sample ID is required');
      return;
    }
    if (!newWorkOrder) {
      toast('error', 'Work Order is required');
      return;
    }
    const duplicate = samples.find(s =>
      String(s.id) === newId && String(s.id) !== originalId
    );
    if (duplicate) {
      toast('error', `Sample ID ${newId} already exists`);
      return;
    }

    const current = samples.find(s => String(s.id) === originalId) || {};
    const proposed = {
      workOrder: newWorkOrder,
      source: String(editModal.source || '').trim(),
      client: String(editModal.client || '').trim(),
      sourceId: String(editModal.sourceId || '').trim(),
      desc: String(editModal.desc || '').trim(),
      attention: String(editModal.attention || '').trim(),
      phone: String(editModal.phone || '').trim(),
      po: String(editModal.po || '').trim(),
      pm: String(editModal.pm || '').trim(),
      subcontractor: String(editModal.subcontractor || '').trim(),
      received: editModal.received || '',
      timeReceived: editModal.timeReceived || '',
      taken: editModal.taken || '',
      dueDate: editModal.dueDate || '',
      testCategory: editModal.testCategory || '',
      testType: editModal.testType || '',
      sampleForm: editModal.sampleForm || '',
      amount: editModal.amount || '',
      solidLiq: editModal.solidLiq || '',
      rush: editModal.rush || '',
      tech: String(editModal.tech || '').trim(),
      completed: editModal.completed === 'Y',
      dateCompleted: editModal.dateCompleted || '',
      price: parseFloat(editModal.price) || 0,
      billed: editModal.billed === 'Y',
      invoiceNum: String(editModal.invoiceNum || '').trim(),
    };
    const categoryChanged = String(current.testCategory || '').trim().toLowerCase() !==
      String(proposed.testCategory || '').trim().toLowerCase();
    if (categoryChanged) {
      proposed.results = resultsWithCategoryUnits(
        current.results,
        current.params || [],
        proposed.testCategory,
        proposed.solidLiq === 'S' ? 'S' : 'L'
      );
      proposed.testType = proposed.testCategory.toLowerCase().includes('fuel')
        ? 'Fuel'
        : proposed.testCategory.toLowerCase().includes('coal') ? 'Coal' : 'Specific';
    }
    const identityChanged = newId !== originalId;

    const performUpdate = async () => {
      setSavingEdit(true);
      try {
        if (identityChanged) {
          await db.updateSampleIdentity(originalId, newId, newWorkOrder);
        }
        const targetId = identityChanged ? newId : originalId;
        await db.updateSample(targetId, proposed);
        const updated = db.getSamples().find(s => String(s.id) === targetId);
        if (updated) onSelectSample(updated);
        setSelected(targetId);
        setEditModal(null);
        refresh();
        toast('success', categoryChanged
          ? `Sample ${targetId} updated; saved results were preserved and units were refreshed for ${proposed.testCategory}`
          : `Sample ${targetId} updated`);
      } catch (error) {
        refresh();
        toast('error', error.message || 'Could not update the sample');
      } finally {
        setSavingEdit(false);
      }
    };

    performUpdate();
  };

  const requestDelete = (sample, event) => {
    event?.stopPropagation();
    if (sample.billed || sample.invoiceNum) {
      toast('error', `Sample ${sample.id} is linked to invoice ${sample.invoiceNum || ''}. Remove it from billing before deletion.`);
      return;
    }
    setDeleteModal({ sample, confirmation:'' });
  };

  const confirmDelete = () => {
    if (!deleteModal || deletingSample) return;
    const sample = deleteModal.sample;
    const sampleId = String(sample.id);
    if (String(deleteModal.confirmation).trim() !== sampleId) {
      toast('error', `Type ${sampleId} exactly to confirm deletion`);
      return;
    }

    const performDelete = async () => {
      setDeletingSample(true);
      try {
        await db.deleteSample(sampleId);
        if (String(selected) === sampleId) {
          setSelected(null);
          onSelectSample(null);
        }
        setDeleteModal(null);
        refresh();
        toast('success', `Sample ${sampleId} deleted`);
      } catch (error) {
        refresh();
        toast('error', error.message || `Could not delete Sample ${sampleId}`);
      } finally {
        setDeletingSample(false);
      }
    };

    setDeleteModal(null);
    approvalCheck({
      sampleId,
      sample,
      changeType: 'delete_sample',
      description: `Permanently delete Sample ${sampleId} (${sample.source || ''}) — ${sample.workOrder || ''}`,
      proposedChanges: { deleteSample:true },
      currentValues: {
        sampleId, workOrder:sample.workOrder, source:sample.source,
        desc:sample.desc, completed:sample.completed, billed:sample.billed,
      },
      onAllowed: performDelete,
    });
  };

  const exportCSV = () => {
    const headers = isAdmin
      ? ['Sample #','Work Order','Rush','Received','Taken','Source','Description','Test Type','Assigned To','Completed','Date Completed','Price','Billed','Invoice #']
      : ['Sample #','Work Order','Rush','Received','Taken','Source','Description','Test Type','Assigned To','Completed','Date Completed'];
    const rows = filtered.map(s => isAdmin
      ? [s.id,s.workOrder,s.rush||'',s.received,s.taken,s.source,s.desc,s.testType,s.tech||'',s.completed?'Y':'N',s.dateCompleted,s.price,s.billed?'Y':'N',s.invoiceNum]
      : [s.id,s.workOrder,s.rush||'',s.received,s.taken,s.source,s.desc,s.testType,s.tech||'',s.completed?'Y':'N',s.dateCompleted]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v||'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `sterling_log_${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const anySearch = Object.values(search).some(v => v);
  const totalBilled = filtered.filter(s => s.billed).reduce((a, s) => a + (s.price || 0), 0);
  const totalPending = filtered.filter(s => !s.billed).reduce((a, s) => a + (s.price || 0), 0);

  return (
    <>
      <div className="toolbar">
        <button className="btn btn-primary" onClick={() => onNavigate('receive')}>＋ New Sample</button>
        <div className="tb-sep"/>
        {FILTERS.map(f => (
          <button key={f} className={`btn ${filter===f?'btn-primary':''}`} onClick={() => setFilter(f)}>{f}</button>
        ))}
        <div className="tb-sep"/>
        <span className="tb-label">Status:</span>
        {COMPLETION_FILTERS.map(status => (
          <button key={status} className={`btn ${completionFilter===status?'btn-primary':''}`}
            onClick={() => setCompletionFilter(status)}>{status}</button>
        ))}
        <div className="tb-sep"/>
        {/* Multi-field search */}
        <input className="search-input" placeholder="Sample ID" title="Search by Sample ID"
          value={search.id} onChange={e => setS('id', e.target.value)} style={{width:82}}/>
        <input className="search-input" placeholder="Work Order" title="Search by Work Order"
          value={search.workOrder} onChange={e => setS('workOrder', e.target.value)} style={{width:100}}/>
        <input className="search-input" placeholder="Source / Client" title="Search by Source or Client"
          value={search.source} onChange={e => setS('source', e.target.value)} style={{width:130}}/>
        <input className="search-input" placeholder="Assigned To"
          value={search.tech} onChange={e=>setSearch(p=>({...p,tech:e.target.value}))} style={{width:120}}/>
        <input className="search-input" placeholder="Description" title="Search by Description"
          value={search.desc} onChange={e => setS('desc', e.target.value)} style={{width:120}}/>
        {anySearch && (
          <button className="btn btn-sm" onClick={clearSearch} title="Clear all filters">✕</button>
        )}
        <div className="tb-sep"/>
        <span className="tb-label">Found:</span><span className="tb-value">{filtered.length}</span>
        {isAdmin && <>
          <span className="tb-label" style={{marginLeft:8}}>Billed:</span>
          <span className="tb-value">${totalBilled.toLocaleString('en-US',{minimumFractionDigits:2})}</span>
        </>}
        <span className="tb-label" style={{marginLeft:8}}>Pending:</span>
        <span className="tb-value">${totalPending.toLocaleString('en-US',{minimumFractionDigits:2})}</span>
        <div className="tb-sep"/>
        <button className="btn" onClick={exportCSV}>⬇ CSV</button>
        <button className="btn" onClick={() => window.print()}>🖨 Print</button>
      </div>

      <div className="content" style={{padding:0}}>
        <div className="table-wrap" style={{height:'100%'}}>
          <table className="data-table">
            <thead>
              <tr>
                {[['id','Sample #'],['workOrder','Work Order'],['rush','Rush'],['received','Received'],
                  ['taken','Taken'],['source','Source'],['desc','Description'],['testType','Test Type'],
                ['tech','Assigned To'],['completed','✓'],['dateCompleted','Date Completed'],
                  ...( isAdmin ? [['price','Price'],['billed','Billed'],['invoiceNum','Invoice #']] : [] )].map(([col,label]) => (
                  <th key={col} className="sortable" onClick={() => sort(col)}>
                    {label}{sortCol===col ? (sortDir==='asc'?' ↑':' ↓') : ''}
                  </th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className={`clickable ${selected===s.id?'selected':''}`}
                  onClick={() => { setSelected(s.id); onSelectSample(s); }}>
                  <td><strong className="mono">{s.id}</strong></td>
                  <td className="mono">{s.workOrder}</td>
                  <td>{s.rush ? <span className="rush-badge">{s.rush}</span> : ''}</td>
                  <td>{s.received}</td>
                  <td>{s.taken}</td>
                  <td style={{maxWidth:150,overflow:'hidden',textOverflow:'ellipsis'}}>{s.source}</td>
                  <td style={{maxWidth:170,overflow:'hidden',textOverflow:'ellipsis'}}>{s.desc}</td>
                  <td><span className={`badge badge-${s.testType?.toLowerCase()}`}>{s.testType}</span></td>
                  <td>
                    {s.tech ? (
                      <span style={{display:'flex',alignItems:'center',gap:5}}>
                        <span style={{width:22,height:22,borderRadius:'50%',background:'#1e3060',
                          color:'white',fontSize:10,fontWeight:700,display:'flex',
                          alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {(s.tech||'?')[0].toUpperCase()}
                        </span>
                        <span style={{fontSize:11,fontWeight:600,color:'#1e3060'}}>{s.tech}</span>
                      </span>
                    ) : <span style={{color:'#ccc',fontSize:11}}>—</span>}
                  </td>
                  <td className="text-center">{s.completed ? '☑' : '☐'}</td>
                  <td>{s.dateCompleted}</td>
                  {isAdmin && <>
                    <td className="text-right font-bold">${(s.price||0).toFixed(2)}</td>
                    <td><span className={`badge ${s.billed?'badge-billed':'badge-pending'}`}>{s.billed?'Billed':'Pending'}</span></td>
                    <td className="mono">{s.invoiceNum}</td>
                  </>}
                  <td style={{display:'flex',gap:3,flexWrap:'nowrap'}}>
                    <button className="btn btn-sm"
                      onClick={e => {
                        e.stopPropagation();
                        setEditModal({
                          ...s,
                          _originalId:s.id,
                          completed:s.completed?'Y':'N',
                          billed:s.billed?'Y':'N',
                        });
                      }}>
                      Edit
                    </button>
                    <button className="btn btn-sm"
                      onClick={e => { e.stopPropagation(); onSelectSample(s); onNavigate('setup'); }}>
                      Setup
                    </button>
                    <button className="btn btn-sm"
                      style={{background:'#edf8ee',borderColor:'#9ac79d',color:'#245b29'}}
                      title="Print compact two-line sample label"
                      disabled={printingLabel === String(s.id)}
                      onClick={e => handlePrintLabel(s, e)}>
                      {printingLabel === String(s.id) ? 'Printing…' : '🏷 Label'}
                    </button>
                    <button className="btn btn-sm" style={{background:'#e8f4ff',borderColor:'#aac8ee',color:'#1a4a80'}}
                      title={`Duplicate sample ${s.id} — copies all parameters and client info`}
                      onClick={e => openDuplicate(s, e)}>
                      ⧉ Dup
                    </button>
                    <button className="btn btn-sm"
                      style={{background:'#eef9f1',borderColor:'#9ac79d',color:'#245b29',fontWeight:600}}
                      title={`Scan or upload a COC for Work Order ${s.workOrder || '—'}`}
                      onClick={e=>{ e.stopPropagation(); setAddCocSample(s); }}>
                      + COC
                    </button>
                    <WorkOrderCOCGallery key={`${s.id}-${cocVersion}`}
                      workOrder={s.workOrder} fallbackCocId={s.cocId} toast={toast}/>
                    <SampleResultsSheetGallery sampleId={s.id}/>
                    <button className="btn btn-sm"
                      style={{background:'#fff0f0',borderColor:'#e2a3a3',color:'#a11b1b',fontWeight:700}}
                      title={s.billed || s.invoiceNum ? 'Remove this sample from billing before deletion' : `Delete Sample ${s.id}`}
                      onClick={e => requestDelete(s, e)}>
                      🗑 Delete
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={14} className="text-center text-muted" style={{padding:24}}>No records found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Edit Modal ── */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" style={{width:860,maxWidth:'96vw'}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Edit Sample Information — {editModal._originalId}</h2>
              <button className="modal-close" onClick={() => setEditModal(null)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'74vh',overflowY:'auto'}}>
              <div className="form-section-title">Identification and Work Order</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Sample ID *</label>
                  <input type="text" value={editModal.id||''}
                    onChange={e=>setEditModal(p=>({...p,id:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Work Order *</label>
                  <input type="text" value={editModal.workOrder||''}
                    onChange={e=>setEditModal(p=>({...p,workOrder:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Source / Company</label>
                  <input type="text" value={editModal.source||''}
                    onChange={e=>setEditModal(p=>({...p,source:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Client Name</label>
                  <input type="text" value={editModal.client||''}
                    onChange={e=>setEditModal(p=>({...p,client:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Source ID</label>
                  <input type="text" value={editModal.sourceId||''}
                    onChange={e=>setEditModal(p=>({...p,sourceId:e.target.value}))}/>
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Description</label>
                  <input type="text" value={editModal.desc||''}
                    onChange={e=>setEditModal(p=>({...p,desc:e.target.value}))}
                    placeholder="Sample description"/>
                </div>
              </div>

              <div className="form-section-title" style={{marginTop:16}}>Client and Contact Information</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Contact / Attention</label>
                  <input type="text" value={editModal.attention||''}
                    onChange={e=>setEditModal(p=>({...p,attention:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="text" value={editModal.phone||''}
                    onChange={e=>setEditModal(p=>({...p,phone:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>PO Number</label>
                  <input type="text" value={editModal.po||''}
                    onChange={e=>setEditModal(p=>({...p,po:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Project Manager</label>
                  <input type="text" value={editModal.pm||''}
                    onChange={e=>setEditModal(p=>({...p,pm:e.target.value}))}/>
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Subcontractor</label>
                  <input type="text" value={editModal.subcontractor||''}
                    onChange={e=>setEditModal(p=>({...p,subcontractor:e.target.value}))}/>
                </div>
              </div>

              <div className="form-section-title" style={{marginTop:16}}>Dates, Assignment and Sample Details</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Date Received</label>
                  <input type="date" value={editModal.received||''}
                    onChange={e=>setEditModal(p=>({...p,received:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Time Received</label>
                  <input type="time" value={editModal.timeReceived||''}
                    onChange={e=>setEditModal(p=>({...p,timeReceived:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Date Taken</label>
                  <input type="date" value={editModal.taken||''}
                    onChange={e=>setEditModal(p=>({...p,taken:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" value={editModal.dueDate||''}
                    onChange={e=>setEditModal(p=>({...p,dueDate:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Assigned To</label>
                  <input type="text" value={editModal.tech||''}
                    onChange={e=>setEditModal(p=>({...p,tech:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Rush Priority</label>
                  <input type="number" min="1" max="10" value={editModal.rush||''}
                    onChange={e=>setEditModal(p=>({...p,rush:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Test Category</label>
                  <input type="text" value={editModal.testCategory||''}
                    onChange={e=>setEditModal(p=>({...p,testCategory:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Test Type</label>
                  <input type="text" value={editModal.testType||''}
                    onChange={e=>setEditModal(p=>({...p,testType:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Sample Form / Matrix</label>
                  <input type="text" value={editModal.sampleForm||''}
                    onChange={e=>setEditModal(p=>({...p,sampleForm:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Sample Amount</label>
                  <input type="text" value={editModal.amount||''}
                    onChange={e=>setEditModal(p=>({...p,amount:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Solid / Liquid</label>
                  <input type="text" value={editModal.solidLiq||''}
                    onChange={e=>setEditModal(p=>({...p,solidLiq:e.target.value}))}/>
                </div>
              </div>

              <div className="form-section-title" style={{marginTop:16}}>Status and Billing</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Completed</label>
                  <select value={editModal.completed} onChange={e=>setEditModal(p=>({...p,completed:e.target.value}))}>
                    <option value="N">No</option><option value="Y">Yes</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date Completed</label>
                  <input type="date" value={editModal.dateCompleted||''}
                    onChange={e=>setEditModal(p=>({...p,dateCompleted:e.target.value}))}/>
                </div>
                {isAdmin && <div className="form-group"><label>Price ($)</label>
                  <input type="number" value={editModal.price||0} step="0.01"
                    onChange={e=>setEditModal(p=>({...p,price:e.target.value}))}/>
                </div>}
                {isAdmin && <>
                  <div className="form-group"><label>Billed</label>
                    <select value={editModal.billed} onChange={e=>setEditModal(p=>({...p,billed:e.target.value}))}>
                      <option value="N">Pending</option><option value="Y">Billed</option>
                    </select>
                  </div>
                  <div className="form-group"><label>Invoice #</label>
                    <input type="text" value={editModal.invoiceNum||''}
                      onChange={e=>setEditModal(p=>({...p,invoiceNum:e.target.value}))}/>
                  </div>
                </>}
              </div>
              {String(editModal.id) !== String(editModal._originalId) && (
                <div className="notice notice-warn" style={{marginTop:14}}>
                  Changing the Sample ID will move its results and linked sample records to the new ID.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? 'Saving…' : '💾 Save All Changes'}
              </button>
              <button className="btn" onClick={() => setEditModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {addCocSample && (
        <AddCOCModal
          sample={addCocSample}
          toast={toast}
          onClose={()=>setAddCocSample(null)}
          onAttached={()=>setCocVersion(v=>v+1)}
        />
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => !deletingSample && setDeleteModal(null)}>
          <div className="modal" style={{width:540}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header" style={{background:'#9f1d1d'}}>
              <h2>🗑 Delete Sample {deleteModal.sample.id}</h2>
              <button className="modal-close" onClick={() => setDeleteModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="notice notice-warn" style={{marginBottom:14}}>
                This removes the sample and its entered results from the active LIMS. Complete-system backups remain available for disaster recovery.
              </div>
              <div style={{fontSize:12,lineHeight:1.6,marginBottom:14}}>
                <strong>Work Order:</strong> {deleteModal.sample.workOrder || '—'}<br/>
                <strong>Source / Company:</strong> {deleteModal.sample.source || '—'}<br/>
                <strong>Description:</strong> {deleteModal.sample.desc || '—'}
              </div>
              <div className="form-group">
                <label>Type the Sample ID <strong>{deleteModal.sample.id}</strong> to confirm</label>
                <input autoFocus type="text" value={deleteModal.confirmation}
                  onChange={e=>setDeleteModal(p=>({...p,confirmation:e.target.value}))}
                  onKeyDown={e=>{ if (e.key==='Enter') confirmDelete(); }}
                  placeholder={String(deleteModal.sample.id)}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={confirmDelete}
                disabled={deletingSample || String(deleteModal.confirmation).trim() !== String(deleteModal.sample.id)}
                style={{background:'#b83232',borderColor:'#b83232',color:'white',fontWeight:700}}>
                {deletingSample ? 'Deleting…' : 'Permanently Delete Sample'}
              </button>
              <button className="btn" onClick={() => setDeleteModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Duplicate Modal ── */}
      {dupModal && (
        <div className="modal-overlay" onClick={() => setDupModal(null)}>
          <div className="modal" style={{width:780}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⧉ Duplicate Sample — copied from #{dupModal._sourceId}</h2>
              <button className="modal-close" onClick={() => setDupModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="notice notice-info" style={{marginBottom:14}}>
                All client info, test parameters ({dupModal.params?.length || 0} params), test category, and pricing have been copied.
                Update the fields below that changed for this new submission.
              </div>

              <div className="form-section-title">New Sample Tracking</div>
              <div className="form-grid" style={{marginBottom:14}}>
                <div className="form-group"><label>New Sample # (auto)</label>
                  <input readOnly value={dupModal.id} style={{background:'#f0f4ff'}}/>
                </div>
                <div className="form-group"><label>Work Order (blank = auto-assign)</label>
                  <input value={dupModal.workOrder}
                    onChange={e=>setDupModal(p=>({...p,workOrder:e.target.value}))}
                    placeholder="Reserved automatically when saved"/>
                </div>
                <div className="form-group"><label>Date Received</label>
                  <input type="date" value={dupModal.received}
                    onChange={e=>setDupModal(p=>({...p,received:e.target.value}))}/>
                </div>
                <div className="form-group"><label>Date Taken</label>
                  <input type="date" value={dupModal.taken}
                    onChange={e=>setDupModal(p=>({...p,taken:e.target.value}))}/>
                </div>
                <div className="form-group"><label>Due Date</label>
                  <input type="date" value={dupModal.dueDate}
                    onChange={e=>setDupModal(p=>({...p,dueDate:e.target.value}))}/>
                </div>
                <div className="form-group"><label>Rush Priority</label>
                  <input type="number" min="1" max="10" value={dupModal.rush||''}
                    onChange={e=>setDupModal(p=>({...p,rush:e.target.value}))}/>
                </div>
              </div>

              <div className="form-section-title">Update If Changed</div>
              <div className="form-grid" style={{marginBottom:14}}>
                <div className="form-group"><label>Source / Client</label>
                  <input value={dupModal.source}
                    onChange={e=>setDupModal(p=>({...p,source:e.target.value}))}/>
                </div>
                <div className="form-group"><label>Description</label>
                  <input value={dupModal.desc}
                    onChange={e=>setDupModal(p=>({...p,desc:e.target.value}))}/>
                </div>
                <div className="form-group"><label>Contact / Attention</label>
                  <input value={dupModal.attention}
                    onChange={e=>setDupModal(p=>({...p,attention:e.target.value}))}/>
                </div>
                <div className="form-group"><label>PO Number</label>
                  <input value={dupModal.po}
                    onChange={e=>setDupModal(p=>({...p,po:e.target.value}))}/>
                </div>
                {isAdmin && <div className="form-group"><label>Price ($)</label>
                  <input type="number" step="0.01" value={dupModal.price}
                    onChange={e=>setDupModal(p=>({...p,price:e.target.value}))}/>
                </div>}
                <div className="form-group"><label>Technician</label>
                  <input value={dupModal.tech}
                    onChange={e=>setDupModal(p=>({...p,tech:e.target.value}))}/>
                </div>
              </div>

              <div className="form-section-title">Carried Over (read-only — edit in Setup)</div>
              <div style={{background:'#f8f9ff',border:'1px solid #e0e4ec',borderRadius:4,padding:'8px 12px',fontSize:11}}>
                <div style={{marginBottom:4}}>
                  <strong>Test Category:</strong> {dupModal.testCategory} &nbsp;|&nbsp;
                  <strong>Sample Form:</strong> {dupModal.sampleForm} &nbsp;|&nbsp;
                  <strong>Amount:</strong> {dupModal.amount} &nbsp;|&nbsp;
                  <strong>S/L:</strong> {dupModal.solidLiq}
                </div>
                <div>
                  <strong>Parameters ({dupModal.params?.length || 0}):</strong>&nbsp;
                  <span style={{color:'#1e3060'}}>
                    {dupModal.params?.length > 0
                      ? dupModal.params.slice(0,8).join(', ') + (dupModal.params.length > 8 ? ` ... +${dupModal.params.length - 8} more` : '')
                      : 'None assigned yet'}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={saveDuplicate} disabled={savingDuplicate}>
                {savingDuplicate ? '⏳ Creating…' : `⧉ Create Duplicate Sample #${dupModal.id}`}
              </button>
              <button className="btn" onClick={() => setDupModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {approvalModal}
    </>
  );
}
